/**
 * Aspect — Time-blended shader chunks (baked lighting pipeline)
 * --------------------------------------------------------------
 * Custom PlayCanvas shader chunks that lerp between DAY and NIGHT baked data
 * from a single global timeline uniform `uTimeBlend` (0.0 = day, 1.0 = night):
 *
 *   lightmapPS       — samples texture_lightMap (day) + texture_lightMapNight
 *                      (both RGBM) and mixes them in linear space.
 *   reflectionEnvPS  — samples the material's prefiltered env atlas (day) +
 *                      texture_envAtlasNight and mixes the IBL result. Runs
 *                      through cubeMapProject(), so the native box projection
 *                      (CUBEPROJ_BOX) applies to both.
 *   ambientPS        — no-op ambient for lightmapped shell materials (the bake
 *                      already contains sky/GI ambient; adding the env-atlas
 *                      ambient on top would double-light the room).
 *
 * `uTimeBlend` is registered in the graphics device scope once per frame by
 * BakedLighting, so every material sharing these chunks blends in lockstep —
 * the same trick the Moka interior demo uses for its day/night slider.
 */

/** Blended day/night lightmap sampling (standard chunk: getLightMap). */
export const LIGHTMAP_TIME_BLEND_PS = /* glsl */ `
uniform sampler2D texture_lightMapNight;
#ifndef ASPECT_TIME_BLEND
#define ASPECT_TIME_BLEND
uniform float uTimeBlend;
#endif

#ifdef STD_LIGHTMAP_DIR
    vec3 dLightmapDir;
    uniform sampler2D texture_dirLightMap;
#endif

void getLightMap() {
    dLightmap = vec3(1.0);

    #ifdef STD_LIGHT_TEXTURE
        vec3 lmDay = {STD_LIGHT_TEXTURE_DECODE}(texture2DBias({STD_LIGHT_TEXTURE_NAME}, {STD_LIGHT_TEXTURE_UV}, textureBias)).{STD_LIGHT_TEXTURE_CHANNEL};
        vec3 lmNight = {STD_LIGHT_TEXTURE_DECODE}(texture2DBias(texture_lightMapNight, {STD_LIGHT_TEXTURE_UV}, textureBias)).{STD_LIGHT_TEXTURE_CHANNEL};
        dLightmap *= mix(lmDay, lmNight, uTimeBlend);
    #endif

    #ifdef STD_LIGHT_VERTEX
        dLightmap *= saturate(vVertexColor.{STD_LIGHT_VERTEX_CHANNEL});
    #endif
}
`;

/**
 * Blended day/night prefiltered-atlas reflections (lit chunk: addReflection).
 * Mirrors the engine's reflectionEnv chunk but samples two atlases. The night
 * atlas rides in via a material parameter; both go through cubeMapProject()
 * so box projection matches the engine path exactly.
 */
export const REFLECTION_ENV_TIME_BLEND_PS = /* glsl */ `
#ifndef ENV_ATLAS
#define ENV_ATLAS
    uniform sampler2D texture_envAtlas;
#endif
uniform sampler2D texture_envAtlasNight;
uniform float material_reflectivity;
#ifndef ASPECT_TIME_BLEND
#define ASPECT_TIME_BLEND
uniform float uTimeBlend;
#endif

float shinyMipLevel(vec2 uv) {
    vec2 dx = dFdx(uv);
    vec2 dy = dFdy(uv);
    vec2 uv2 = vec2(fract(uv.x + 0.5), uv.y);
    vec2 dx2 = dFdx(uv2);
    vec2 dy2 = dFdy(uv2);
    float maxd = min(max(dot(dx, dx), dot(dy, dy)), max(dot(dx2, dx2), dot(dy2, dy2)));
    return clamp(0.5 * log2(maxd) - 1.0 + textureBias, 0.0, 5.0);
}

vec3 sampleAtlas(sampler2D atlas, vec2 uv0, vec2 uv1, float weight, vec2 uvRough, float levelFrac) {
    vec3 linearA = {reflectionDecode}(texture2D(atlas, uv0));
    vec3 linearB = {reflectionDecode}(texture2D(atlas, uv1));
    vec3 linear0 = mix(linearA, linearB, weight);
    vec3 linear1 = {reflectionDecode}(texture2D(atlas, uvRough));
    return mix(linear0, linear1, levelFrac);
}

vec3 calcReflection(vec3 reflDir, float gloss) {
    vec3 dir = cubeMapProject(reflDir) * vec3(-1.0, 1.0, 1.0);
    vec2 uv = toSphericalUv(dir);

    float level = saturate(1.0 - gloss) * 5.0;
    float ilevel = floor(level);
    float level2 = shinyMipLevel(uv * atlasSize);
    float ilevel2 = floor(level2);

    vec2 uv0, uv1;
    float weight;
    if (ilevel == 0.0) {
        uv0 = mapShinyUv(uv, ilevel2);
        uv1 = mapShinyUv(uv, ilevel2 + 1.0);
        weight = level2 - ilevel2;
    } else {
        uv0 = uv1 = mapRoughnessUv(uv, ilevel);
        weight = 0.0;
    }
    vec2 uvRough = mapRoughnessUv(uv, ilevel + 1.0);
    float levelFrac = level - ilevel;

    vec3 day = sampleAtlas(texture_envAtlas, uv0, uv1, weight, uvRough, levelFrac);
    vec3 night = sampleAtlas(texture_envAtlasNight, uv0, uv1, weight, uvRough, levelFrac);
    return processEnvironment(mix(day, night, uTimeBlend));
}

void addReflection(vec3 reflDir, float gloss) {
    dReflection += vec4(calcReflection(reflDir, gloss), material_reflectivity);
}
`;

/**
 * No-op ambient for lightmapped shell materials — GI ambient already lives in
 * the baked lightmaps, so the env-atlas ambient term must not be added twice.
 */
export const AMBIENT_ZERO_PS = /* glsl */ `
void addAmbient(vec3 worldNormal) {
    dDiffuseLight += vec3(0.0);
}
`;

/** Chunk API version tag written to materials we override. */
export const CHUNK_API_VERSION = "2.11";

/**
 * Install the time-blend chunk set on a lightmapped static-shell material.
 * @param {import('playcanvas').StandardMaterial} mat
 */
export function applyShellChunks(mat) {
    mat.shaderChunks.version = CHUNK_API_VERSION;
    mat.shaderChunks.glsl.set("lightmapPS", LIGHTMAP_TIME_BLEND_PS);
    mat.shaderChunks.glsl.set("ambientPS", AMBIENT_ZERO_PS);
    mat.shaderChunks.glsl.set("reflectionEnvPS", REFLECTION_ENV_TIME_BLEND_PS);
}

/**
 * Install the blended-reflection chunk on a dynamic (furniture) material.
 * Ambient stays native: those materials use PlayCanvas's SH L2 ambient
 * (material.ambientSH) fed per-instance by the probe field.
 * @param {import('playcanvas').StandardMaterial} mat
 */
export function applyFurnitureChunks(mat) {
    mat.shaderChunks.version = CHUNK_API_VERSION;
    mat.shaderChunks.glsl.set("reflectionEnvPS", REFLECTION_ENV_TIME_BLEND_PS);
}

/**
 * Remove every chunk override installed by this module.
 * @param {import('playcanvas').StandardMaterial} mat
 */
export function removeTimeBlendChunks(mat) {
    if (!mat.hasShaderChunks) return;
    mat.shaderChunks.glsl.delete("lightmapPS");
    mat.shaderChunks.glsl.delete("ambientPS");
    mat.shaderChunks.glsl.delete("reflectionEnvPS");
}
